package guide.kuki.app.engine

import kotlin.math.round
import kotlin.math.sqrt

private const val ALPHABET = "abcdefghijklmnopqrstuvwxyz"

/**
 * A quadrilateral (or triangle for sector tips) defined by 4 corner [Coords].
 *
 * Point ordering:
 *   pts[0] = inner-left,  pts[1] = outer-left
 *   pts[2] = inner-right, pts[3] = outer-right
 *
 * For sector tips where inner edges converge, pts[1] == pts[3].
 */
class Block(
    var idx: Int = -1,
    val pts: Array<Coords> = emptyArray(),
) {
    val isValid: Boolean = pts.isNotEmpty() && pts[0].lat != 0.0

    var slice: Slice? = null
    private var _latLng: LatLng? = null
    var address: String = ""
    var subLetters: String = ""
    var divs: IntArray? = null
    private var _subBlock: Block? = null

    fun isSector(): Boolean = pts.size >= 4 && pts[1] == pts[3]

    fun updateLatLng(latlng: LatLng) {
        val current = _latLng
        if (current == null || current.lat != latlng.lat || current.lng != latlng.lng) {
            _subBlock = null
        }
        _latLng = latlng
    }

    fun resolveLatLng(): LatLng {
        val current = _latLng
        if (current != null) return current

        var lat = 0.0
        var lng = 0.0
        for (pt in pts) {
            lat += pt.lat
            lng += pt.lon
        }
        lat /= pts.size
        lng /= pts.size

        val result = LatLng(lat, lng)
        _latLng = result
        return result
    }

    fun getShapeString(): String {
        val dc = distance(pts[0].latlon, pts[2].latlon)
        val dr = distance(pts[0].latlon, pts[1].latlon)
        val dcStr = if (dc >= 10) dc.toInt().toString() else ((dc * 10).toInt() / 10.0).toString()
        val drStr = if (dr >= 10) dr.toInt().toString() else ((dr * 10).toInt() / 10.0).toString()
        return "$dcStr x $drStr m"
    }

    fun containsLatLon(latLon: LatLng): Boolean {
        val pt = doubleArrayOf(latLon.lng, latLon.lat)
        return if (isSector()) {
            toLeftOfLine(pt, pts[0].lonlat, pts[1].lonlat)
                    && toLeftOfLine(pt, pts[1].lonlat, pts[2].lonlat)
                    && toLeftOfLine(pt, pts[2].lonlat, pts[0].lonlat)
        } else {
            toLeftOfLine(pt, pts[0].lonlat, pts[1].lonlat)
                    && toLeftOfLine(pt, pts[1].lonlat, pts[3].lonlat)
                    && toLeftOfLine(pt, pts[3].lonlat, pts[2].lonlat)
                    && toLeftOfLine(pt, pts[2].lonlat, pts[0].lonlat)
        }
    }

    fun calcDivs(): IntArray {
        val dc = distance(pts[0].latlon, pts[2].latlon)
        val dr = distance(pts[0].latlon, pts[1].latlon)

        val charLength = 1.0 / sqrt(dr * dc / 26.0)
        var nr = round(dr * charLength).toInt()
        var nc = round(dc * charLength).toInt()

        if (nr > 26) nr = 26
        if (nr == 11 || nr == 12) nr = 13
        while (nc * nr > 26) {
            nc -= 1
        }

        return intArrayOf(nr, nc)
    }

    fun getPolygon(): List<DoubleArray> {
        return if (isSector()) {
            listOf(pts[0].latlon, pts[1].latlon, pts[2].latlon)
        } else {
            listOf(pts[0].latlon, pts[1].latlon, pts[3].latlon, pts[2].latlon)
        }
    }

    fun getSlicePolygon(): List<List<DoubleArray>> {
        val polygons = mutableListOf<List<DoubleArray>>()
        val s = slice ?: return polygons

        if (s.isSector()) {
            polygons.add(listOf(pts[1].latlon, s.r0[s.lastIdx].latlon, pts[3].latlon))
        } else {
            polygons.add(listOf(pts[1].latlon, s.r0[s.lastIdx].latlon, s.r1[s.lastIdx].latlon, pts[3].latlon))
        }
        polygons.add(listOf(s.r0[0].latlon, pts[0].latlon, pts[2].latlon, s.r1[0].latlon))
        return polygons
    }

    fun calcSubBlock(rIdx: Int, cIdx: Int, rDivs: Int, cDivs: Int): Block {
        val dXi = 1.0 / rDivs
        val xiA = rIdx * dXi
        val xiB = xiA + dXi

        val radialBlock = arrayOf(
            interpolateXi(xiA, pts[0].latlon, pts[1].latlon),
            interpolateXi(xiB, pts[0].latlon, pts[1].latlon),
            interpolateXi(xiA, pts[2].latlon, pts[3].latlon),
            interpolateXi(xiB, pts[2].latlon, pts[3].latlon)
        )

        val dXiC = 1.0 / cDivs
        val xiC0 = cIdx * dXiC
        val xiC1 = xiC0 + dXiC

        val subBlockPts = arrayOf(
            Coords(interpolateXi(xiC0, radialBlock[0], radialBlock[2])),
            Coords(interpolateXi(xiC0, radialBlock[1], radialBlock[3])),
            Coords(interpolateXi(xiC1, radialBlock[0], radialBlock[2])),
            Coords(interpolateXi(xiC1, radialBlock[1], radialBlock[3]))
        )
        val subIdx = cIdx * rDivs + rIdx
        return Block(subIdx, subBlockPts)
    }

    fun getSubBlockByLetter(letter: Char): Block {
        if (divs == null) divs = calcDivs()
        val d = divs!!
        val letterIdx = ALPHABET.indexOf(letter)
        val cIdx = letterIdx / d[0]
        val rIdx = letterIdx - cIdx * d[0]
        val sub = calcSubBlock(rIdx, cIdx, d[0], d[1])
        sub.resolveLatLng()
        sub.subLetters = subLetters + letter
        return sub
    }

    fun resolveSubBlock(): Block {
        val cached = _subBlock
        if (cached != null) return cached

        val latlng = resolveLatLng()
        if (divs == null) divs = calcDivs()
        val d = divs!!
        val nr = d[0]
        val nc = d[1]

        val pt = doubleArrayOf(latlng.lng, latlng.lat)
        var idxR = nr - 1
        var idxC = nc - 1

        for (i in 0 until nr) {
            val block = calcSubBlock(i, 0, nr, 1)
            if (toLeftOfLine(pt, block.pts[1].lonlat, block.pts[3].lonlat)) {
                idxR = i
                break
            }
        }
        for (i in 0 until nc) {
            val block = calcSubBlock(0, i, 1, nc)
            if (toLeftOfLine(pt, block.pts[3].lonlat, block.pts[2].lonlat)) {
                idxC = i
                break
            }
        }

        val result = calcSubBlock(idxR, idxC, nr, nc)
        result.updateLatLng(latlng)
        val sIdx = result.idx
        result.subLetters = subLetters + ALPHABET[sIdx]
        _subBlock = result
        return result
    }

    fun clearSubBlock() {
        _subBlock = null
    }
}
